import $ from "jquery"
import React, { useEffect, useState } from "react"
import { FormattedMessage, injectIntl } from "react-intl"
import Icon from "../../../common/icon/icon.component"
import LoadingSpinner from "../../../common/spinner/loading.component"
import CreditMemo from "../../../state/credit-memo/CreditMemo"
import CreditMemoService from "../../../state/credit-memo/CreditMemoService"
import { HttpError, isError } from "../../../state/helpers/Error"
import IntlProps from "../../../state/helpers/IntlProps"
import { BankDetails, CreditCardDetails } from "../../../state/helpers/Payment"
import { REFUND_TYPE } from "../../../state/invoice/Constants"
import { InvoiceDto } from "../../../state/invoice/models"
import Invoice from "../../../state/invoice/models/invoice"
import { InvoiceService } from "../../../state/invoice/services"
import Payment from "../../../state/payment/Payment"
import PaymentTransaction from "../../../state/payment/PaymentTransaction"
import RefundPayments from "../../../state/payment/refund/RefundPayments"
import RefundConfirmationTable, { RefundConfirmationRow } from "../RefundConfirmationTable/RefundConfirmationTable"
import "./RefundConfirmationModal.scss"

const uuidv4 = require("uuid/v4")

type OwnProps = {
    refundType: REFUND_TYPE,
    refundResponse: RefundPayments | HttpError,
    return?: InvoiceDto,
    creditMemoId?: string,
    onClose: () => void,
}

interface PaymentDetails {
    identifier: string,
    currency: string,
    creditCardDetails?: CreditCardDetails,
    bankAccountDetails?: BankDetails,
    paymentType: string,
    integrationKey: string,
}

const MODAL_ID = "payment-refund-modal-" + uuidv4()

const RefundConfirmationModal = (props: OwnProps & IntlProps) => {
    const [loadingPayments, setLoadingPayments] = useState<boolean>(false)
    const [paymentDetailsMap, setPaymentDetailsMap] = useState<Map<string, PaymentDetails>>(new Map())

    useEffect(() => {
        const populatePaymentDetailsMap = async (invoice: Invoice) => {
            setLoadingPayments(true)
            const associatedInvoice = await InvoiceService.get(invoice.getReturnOfInvoiceId() || invoice.getId())

            if (isError(associatedInvoice)) {
                return
            }

            const detailsMap = new Map()
            const paymentTransactions = (associatedInvoice as Invoice).getPaymentTransactions()
            paymentTransactions.forEach(paymentTransaction => {
                detailsMap.set(
                    paymentTransaction.getPayment().getId(),
                    mapPaymentTransactionToPaymentDetails(paymentTransaction)
                )
            })
            setPaymentDetailsMap(detailsMap)
            setLoadingPayments(false)
        }


        if (props.return && props.return.attributes.returnOfInvoiceId || props.return?.id) {
            populatePaymentDetailsMap(new Invoice(props.return))
        }
    }, [props.return])

    useEffect(() => {
        const populatePaymentDetailsMap = async (id: string) => {
            setLoadingPayments(true)
            const creditMemo = await CreditMemoService.get(id)

            if (isError(creditMemo)) {
                return
            }

            const detailsMap = new Map()
            const payment = (creditMemo as CreditMemo).getPayment()
            detailsMap.set(
                payment.getId(),
                mapPaymentToPaymentDetails(payment)
            )
            setPaymentDetailsMap(detailsMap)
            setLoadingPayments(false)
        }

        if (props.creditMemoId) {
            populatePaymentDetailsMap(props.creditMemoId)
        }
    }, [props.creditMemoId])

    useEffect(() => {
        $("#" + MODAL_ID).modal("show")
    }, [MODAL_ID])

    const mapPaymentTransactionToPaymentDetails = (paymentTransaction: PaymentTransaction): PaymentDetails => {
        const payment = paymentTransaction.getPayment()
        const creditMemoPayment = paymentTransaction.getCreditMemoPayment()

        if (creditMemoPayment) {
            return {
                ...mapPaymentToPaymentDetails(payment),
                creditCardDetails: creditMemoPayment.getIntegratedPaymentCreditCardDetails(),
                bankAccountDetails: creditMemoPayment.getIntegratedPaymentBankDetails(),
                paymentType: creditMemoPayment.getType(),
                integrationKey: creditMemoPayment.getIntegratedPaymentIntegrationKey(),
            }
        } else {
            return mapPaymentToPaymentDetails(payment)
        }
    }

    const mapPaymentToPaymentDetails = (payment: Payment): PaymentDetails => ({
        identifier: payment.getIdentifier(),
        currency: payment.getCurrency(),
        creditCardDetails: payment.getIntegratedPaymentCreditCardDetails(),
        bankAccountDetails: payment.getIntegratedPaymentBankDetails(),
        paymentType: payment.getType(),
        integrationKey: payment.getIntegratedPaymentIntegrationKey(),
    })

    const handleClose = event => {
        event.preventDefault()
        $("#" + MODAL_ID).modal("hide")
        if (props.onClose) {
            props.onClose()
        }
    }

    const getTableRows = (): RefundConfirmationRow[] => {
        if (!isError(props.refundResponse)) {
            const currency = props.refundResponse.getCurrency()
            return props.refundResponse.getRefundedPayments().map(payment => {
                const paymentDetails = paymentDetailsMap.get(payment.getRefundedPaymentId())
                return paymentDetails
                    ? {
                        refundPaymentId: payment.getId(),
                        refundPaymentIdentifier: payment.getIdentifier(),
                        refundedPaymentId: payment.getRefundedPaymentId(),
                        refundedAmount: `${payment.getAmount()}`,
                        refundedMethodType: payment.getRefundMethod(),
                        currency: currency,
                        refundedPaymentIdentifier: paymentDetails.identifier,
                        creditCardDetails: paymentDetails.creditCardDetails,
                        bankAccountDetails: paymentDetails.bankAccountDetails,
                        paymentType: paymentDetails.paymentType,
                        integrationKey: paymentDetails.integrationKey,
                        status: payment.getStatus(),
                    }
                    : {
                        refundPaymentId: payment.getId(),
                        refundPaymentIdentifier: payment.getIdentifier(),
                        refundedAmount: `${payment.getAmount()}`,
                        refundedMethodType: payment.getRefundMethod(),
                        currency: currency,
                        status: payment.getStatus(),
                    }
            })
        }
        return []
    }

    return (
        <div
            className="modal fade refund-confirmation-modal"
            id={MODAL_ID}
            role="dialog"
            aria-labelledby="refundModalTitle"
            data-backdrop="static"
            data-keyboard="false"
            tabIndex={-1}
        >
            <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title mb-0 border-0 pb-0" id="refundModalTitle">
                            <Icon value="undo" className="mr-2" />
                            <FormattedMessage
                                id="refund_confirmation_modal.title"
                                defaultMessage="Refund confirmation"
                            />
                        </h5>
                        <button type="button" className="close" onClick={handleClose} aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <div className="modal-body">
                        {loadingPayments && <LoadingSpinner />}
                        {!loadingPayments && !isError(props.refundResponse) && (
                            <>
                                <RefundConfirmationTable
                                    title={
                                        <FormattedMessage
                                            id="refund_confirmation_modal.refund_confirmation_table.payment_from_refunds.title"
                                            defaultMessage="Refunds that were applied"
                                        />
                                    }
                                    refundType={props.refundType}
                                    rows={getTableRows()}
                                    refundMethods={[]}
                                />
                            </>
                        )}
                        {!loadingPayments && isError(props.refundResponse) && (
                            <>
                                <p>
                                    <FormattedMessage
                                        id="refund_confirmation_modal.refund_error"
                                        defaultMessage="The following error occured when trying to process the payment refunds:"
                                    />
                                </p>
                                <p className="ml-3 text-danger">{props.refundResponse.message}</p>
                            </>
                        )}
                    </div>

                    <div className="modal-footer">
                        {!isError(props.refundResponse) && (
                            <div className="form-group row left-to-refund">
                                <table className="table">
                                    <tbody>
                                        <tr>
                                            <td className="label-col">
                                                <b>
                                                    <FormattedMessage
                                                        id="refund_confirmation_modal.total"
                                                        defaultMessage="Total"
                                                    />
                                                </b>
                                                <span>
                                                    <FormattedMessage
                                                        id="refund_confirmation_modal.refunded"
                                                        defaultMessage="refunded"
                                                    />
                                                </span>
                                            </td>
                                            <td className="total-col">
                                                <b>{props.refundResponse.getRefundedTotal() || props.refundResponse.getTotal()}</b>
                                            </td>
                                            <td className="spacer-col" />
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="actions">
                            <button type="button" className="btn btn-secondary" onClick={handleClose}>
                                <FormattedMessage
                                    id="refund_confirmation_modal.close_btn"
                                    defaultMessage="Close"
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default injectIntl(RefundConfirmationModal)


// WEBPACK FOOTER //
// ./src/invoice/components/RefundConfirmationModal/RefundConfirmationModal.tsx